/**
 * Schedule-config knob registry — the Engine screen's single source of truth
 * for which knobs exist, how they're grouped, and how they render
 * (WSRPDM-0014, contract §2.1). Adding a knob later means one `SCHEDULE_KNOBS`
 * entry + one label key — `src/routes/engine/` renders every card generically
 * off this registry, never a per-knob JSX block.
 *
 * NOTE ON COUNT: the original contract's prose said "27 writable knobs" /
 * "the 27 knobs + 4 flags", but its own §2.1 field enumeration — grouped as
 * "Cadences (8) / Retention (3) / Tuning-duration (7) / Tuning-counts (8) /
 * Tuning-other (2)" — summed to 28. WSRPDM-0082 adds two Tuning-count knobs
 * (`failureBreakerMaxFailures`, `failureBreakerWindowHours`), so this file
 * enumerates 30 writable knobs: Cadences (8) / Retention (3) /
 * Tuning-duration (7) / Tuning-counts (10) / Tuning-other (2). Omitting a
 * real knob from the full-set round-trip (§2.2) would silently reset it to
 * default, which is the highest-consequence mistake available on this form.
 */
import type { PlatformScheduleConfigUpdate, PlatformScheduleConfigView } from '@/api/wsr/types';

export type KnobGroup = 'cadence' | 'retention' | 'tuning';
export type KnobUnit = 'seconds' | 'count' | 'enum' | 'bool';
export type EngineHelpScope =
  'allJobs' | 'genesisPacing' | 'outboundRate' | 'platform' | 'attendanceOnly';

/** The writable knob keys — every `PlatformScheduleConfigUpdate` key. */
export type ScheduleKnobKey = Exclude<keyof PlatformScheduleConfigUpdate, EnableFlagKey>;

export type EnableFlagKey =
  'engineEnabled' | 'discoveryEnabled' | 'pruneEnabled' | 'orphanSweepEnabled';

export interface ScheduleKnobDef {
  key: ScheduleKnobKey;
  group: KnobGroup;
  unit: KnobUnit;
  labelKey: string;
  restartRequired: boolean;
  helpScope: EngineHelpScope;
  emphasisKey?: string;
}

export interface EnableFlagDef {
  key: EnableFlagKey;
  labelKey: string;
  fallbackLabel: string;
  descriptionKey: string;
  fallbackDescription: string;
  helpScope: EngineHelpScope;
}

export const ENGINE_HELP_SCOPE_FALLBACK: Readonly<Record<EngineHelpScope, string>> = {
  allJobs: 'All jobs',
  genesisPacing: 'Genesis pacing',
  outboundRate: 'Outbound rate',
  platform: 'Platform',
  attendanceOnly: 'Attendance only',
};

/** Contract §2.1 — the fixed restart-required set. */
export const RESTART_REQUIRED_FIELDS: readonly string[] = [
  'maxConcurrent',
  'isolation',
  'rateGlobalPermits',
  'rateGlobalPeriodSeconds',
];

/** Contract §2.2 — the `isolation` enum (a Spring `Isolation` name). */
export const ISOLATION_VALUES: readonly string[] = [
  'DEFAULT',
  'READ_UNCOMMITTED',
  'READ_COMMITTED',
  'REPEATABLE_READ',
  'SERIALIZABLE',
];

export const ENABLE_FLAGS: readonly EnableFlagDef[] = [
  {
    key: 'engineEnabled',
    labelKey: 'engine.flags.engineEnabled',
    fallbackLabel: 'Engine',
    descriptionKey: 'engine.flags.engineEnabledDescription',
    fallbackDescription: 'The dispatcher/claimer/heartbeat/reaper loop that runs jobs.',
    helpScope: 'allJobs',
  },
  {
    key: 'discoveryEnabled',
    labelKey: 'engine.flags.discoveryEnabled',
    fallbackLabel: 'Discovery',
    descriptionKey: 'engine.flags.discoveryEnabledDescription',
    fallbackDescription: 'The saas-auth tenant-roster reconciliation sweep.',
    helpScope: 'platform',
  },
  {
    key: 'pruneEnabled',
    labelKey: 'engine.flags.pruneEnabled',
    fallbackLabel: 'Prune',
    descriptionKey: 'engine.flags.pruneEnabledDescription',
    fallbackDescription: 'Terminal-job and event retention cleanup.',
    helpScope: 'platform',
  },
  {
    key: 'orphanSweepEnabled',
    labelKey: 'engine.flags.orphanSweepEnabled',
    fallbackLabel: 'Orphan sweep',
    descriptionKey: 'engine.flags.orphanSweepEnabledDescription',
    fallbackDescription:
      'Deletes leftover temporary files from the attendance pipeline, which downloads a generated spreadsheet to disk and normally removes it immediately. This sweep is the backstop for a process killed mid-download. It does not touch jobs or data.',
    helpScope: 'attendanceOnly',
  },
];

function knob(
  key: ScheduleKnobKey,
  group: KnobGroup,
  unit: KnobUnit,
  labelKey: string,
  helpScope: EngineHelpScope,
  emphasisKey?: string,
): ScheduleKnobDef {
  return {
    key,
    group,
    unit,
    labelKey,
    restartRequired: RESTART_REQUIRED_FIELDS.includes(key),
    helpScope,
    ...(emphasisKey ? { emphasisKey } : {}),
  };
}

/** Contract §2.1 — the 30 writable (non-flag) knobs, grouped for the screen's three cards. */
export const SCHEDULE_KNOBS: readonly ScheduleKnobDef[] = [
  // Cadences (8) — integer seconds.
  knob(
    'dispatcherIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.dispatcherIntervalSeconds',
    'genesisPacing',
    'engine.emphasis.dispatcherInterval',
  ),
  knob(
    'claimerIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.claimerIntervalSeconds',
    'allJobs',
  ),
  knob(
    'heartbeatIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.heartbeatIntervalSeconds',
    'allJobs',
  ),
  knob(
    'reaperIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.reaperIntervalSeconds',
    'allJobs',
  ),
  knob(
    'terminalCleanupIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.terminalCleanupIntervalSeconds',
    'platform',
  ),
  knob(
    'discoveryIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.discoveryIntervalSeconds',
    'platform',
  ),
  knob(
    'pruneIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.pruneIntervalSeconds',
    'platform',
  ),
  knob(
    'orphanSweepIntervalSeconds',
    'cadence',
    'seconds',
    'engine.knob.orphanSweepIntervalSeconds',
    'attendanceOnly',
  ),
  // Retention / age (3) — integer seconds.
  knob(
    'terminalRetentionSeconds',
    'retention',
    'seconds',
    'engine.knob.terminalRetentionSeconds',
    'platform',
  ),
  knob(
    'eventRetentionSeconds',
    'retention',
    'seconds',
    'engine.knob.eventRetentionSeconds',
    'platform',
  ),
  knob(
    'orphanSweepAgeSeconds',
    'retention',
    'seconds',
    'engine.knob.orphanSweepAgeSeconds',
    'attendanceOnly',
  ),
  // Tuning — duration (7) — integer seconds.
  knob(
    'defaultFrequencySeconds',
    'tuning',
    'seconds',
    'engine.knob.defaultFrequencySeconds',
    'allJobs',
  ),
  knob('leaseTtlSeconds', 'tuning', 'seconds', 'engine.knob.leaseTtlSeconds', 'allJobs'),
  knob(
    'maxExecutionTimeSeconds',
    'tuning',
    'seconds',
    'engine.knob.maxExecutionTimeSeconds',
    'allJobs',
  ),
  knob(
    'retryBackoffBaseSeconds',
    'tuning',
    'seconds',
    'engine.knob.retryBackoffBaseSeconds',
    'allJobs',
  ),
  knob(
    'retryBackoffCapSeconds',
    'tuning',
    'seconds',
    'engine.knob.retryBackoffCapSeconds',
    'allJobs',
  ),
  knob(
    'ratePerTenantPeriodSeconds',
    'tuning',
    'seconds',
    'engine.knob.ratePerTenantPeriodSeconds',
    'outboundRate',
  ),
  knob(
    'rateGlobalPeriodSeconds',
    'tuning',
    'seconds',
    'engine.knob.rateGlobalPeriodSeconds',
    'outboundRate',
  ),
  // Tuning — counts (10).
  knob('maxAttempts', 'tuning', 'count', 'engine.knob.maxAttempts', 'allJobs'),
  knob('maxRescues', 'tuning', 'count', 'engine.knob.maxRescues', 'allJobs'),
  knob('batchClaimSize', 'tuning', 'count', 'engine.knob.batchClaimSize', 'allJobs'),
  knob(
    'perTenantInFlightCap',
    'tuning',
    'count',
    'engine.knob.perTenantInFlightCap',
    'genesisPacing',
    'engine.emphasis.inFlightCap',
  ),
  knob('maxConcurrent', 'tuning', 'count', 'engine.knob.maxConcurrent', 'allJobs'),
  knob('failureAlertThreshold', 'tuning', 'count', 'engine.knob.failureAlertThreshold', 'allJobs'),
  knob(
    'failureBreakerMaxFailures',
    'tuning',
    'count',
    'engine.knob.failureBreakerMaxFailures',
    'allJobs',
    'engine.emphasis.failureBreakerMaxFailures',
  ),
  knob(
    'failureBreakerWindowHours',
    'tuning',
    'count',
    'engine.knob.failureBreakerWindowHours',
    'allJobs',
    'engine.emphasis.failureBreakerWindowHours',
  ),
  knob(
    'ratePerTenantPermits',
    'tuning',
    'count',
    'engine.knob.ratePerTenantPermits',
    'outboundRate',
  ),
  knob('rateGlobalPermits', 'tuning', 'count', 'engine.knob.rateGlobalPermits', 'outboundRate'),
  // Tuning — other (2).
  knob('skipLockedClaim', 'tuning', 'bool', 'engine.knob.skipLockedClaim', 'allJobs'),
  knob('isolation', 'tuning', 'enum', 'engine.knob.isolation', 'allJobs'),
];

/** count knobs with a floor of 0 (`rateGlobalPermits` plus the two failure-breaker knobs). */
export const COUNT_MIN_ZERO: readonly ScheduleKnobKey[] = [
  'rateGlobalPermits',
  'failureBreakerMaxFailures',
  'failureBreakerWindowHours',
];

/** The 3-field invariant (contract §2.2): heartbeat < lease < maxExecution. */
export const INVARIANT_FIELDS: readonly [ScheduleKnobKey, ScheduleKnobKey, ScheduleKnobKey] = [
  'heartbeatIntervalSeconds',
  'leaseTtlSeconds',
  'maxExecutionTimeSeconds',
];

/**
 * Read every registry knob's current value off a `PlatformScheduleConfigView`
 * (the GET/PUT re-read shape, which is a strict superset of the update body).
 */
export function knobValue(
  view: PlatformScheduleConfigView,
  key: ScheduleKnobKey,
): number | string | boolean {
  return view[key as keyof PlatformScheduleConfigView] as number | string | boolean;
}

/**
 * Defensive label fallback for `useCopy()`'s `fallback` arg — used only if a
 * locale catalog is somehow missing a `labelKey` entry (the four shipped
 * locales always carry one). Derives a readable label straight from the
 * field name so an unmapped/new knob still renders something sane instead of
 * the raw camelCase key.
 */
export function humanizeKnobKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  const trimmed = spaced.replace(/ seconds$/, '');
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}
